import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSearch, faFileExport, faSync } from '@fortawesome/free-solid-svg-icons';
import * as XLSX from 'xlsx';
import UploadDashboard from './UploadDashboard';
import './UploadTableModal.css';

/**
 * A modal component that displays a table of all uploads.
 *
 * @param {boolean} isOpen - Whether the modal is open or not.
 * @param {function} onClose - A callback to call when the modal is closed.
 */
function UploadTableModal({ isOpen, onClose }) {
  const [allUploads, setAllUploads] = useState([]);
  const [displayedUploads, setDisplayedUploads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [isClosing, setIsClosing] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isFiltering, setIsFiltering] = useState(false);
  const [activeTab, setActiveTab] = useState('table');
  
  const ITEMS_PER_PAGE = 100;
  const tableContainerRef = useRef(null);
  
  // Fetch uploads when the modal is opened
  useEffect(() => {
    if (isOpen) {
      fetchUploads();
    }
  }, [isOpen]);
  
  // Reset pagination when search or filter changes
  useEffect(() => {
    setIsFiltering(true);
    setPage(0);
    applyFilters();
  }, [search, statusFilter]);
  
  // Load more items when page changes
  useEffect(() => {
    if (!isFiltering) {
      loadMoreItems();
    }
  }, [page]);
  
  const fetchUploads = async () => {
    try {
      setLoading(true);
      setError(null);
      setDisplayedUploads([]);
      setHasMore(true);
      
      const response = await fetch('https://service9.szapfs.org/all-uploads');
      
      if (!response.ok) {
        throw new Error(`Failed to fetch uploads: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      setAllUploads(data);
      
      // Directly load the first page of items instead of using useEffect
      const firstPage = data.slice(0, ITEMS_PER_PAGE);
      setDisplayedUploads(firstPage);
      setHasMore(firstPage.length < data.length);
      setIsFiltering(false);
    } catch (err) {
      console.error('Error fetching uploads:', err);
      setError(err.message);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };
  
  const applyFilters = () => {
    setIsFiltering(true);
    setDisplayedUploads([]);
    
    // We'll load the first page of filtered results
    loadMoreItems(true);
  };
  
  const loadMoreItems = useCallback((isNewFilter = false) => {
    if (loading) return;
    
    const normalized = (text) => text?.toString().toLowerCase() || '';
    const filtered = allUploads.filter(u =>
      (!statusFilter || u.status === statusFilter) &&
      (!search ||
        normalized(u.patientId).includes(normalized(search)) ||
        normalized(JSON.stringify(u.newFileName)).includes(normalized(search))
      )
    );
    
    const start = isNewFilter ? 0 : displayedUploads.length;
    const end = start + ITEMS_PER_PAGE;
    const newItems = filtered.slice(start, end);
    
    if (isNewFilter) {
      setDisplayedUploads(newItems);
    } else {
      setDisplayedUploads(prev => [...prev, ...newItems]);
    }
    
    setHasMore(end < filtered.length);
    setIsFiltering(false);
  }, [allUploads, search, statusFilter, displayedUploads.length, loading]);
  
  // Detect when user scrolls near bottom of table
  const handleScroll = useCallback(() => {
    if (!tableContainerRef.current || loading || !hasMore || isFiltering) return;
    
    const { scrollTop, scrollHeight, clientHeight } = tableContainerRef.current;
    // Load more when user scrolls to bottom (with a 200px buffer)
    if (scrollHeight - scrollTop - clientHeight < 200) {
      setPage(prevPage => prevPage + 1);
    }
  }, [loading, hasMore, isFiltering]);
  
  // Add scroll event listener
  useEffect(() => {
    const container = tableContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  const statusColors = {
    Failed: '#dc3545',
    Completed: '#28a745',
    Complete: '#28a745',
    Queued: '#6c757d',
    Processing: '#ffc107',
    Uploading: '#007bff',
    Downloading: '#0dcaf0',
    DownloadFailed: '#dc3545'
  };



  if (!isOpen) return null;
  
  // Create portal content to render directly to document body

  // Export ALL data, not just what's displayed
  const exportToExcel = () => {
    // Apply filters to all uploads to get complete filtered dataset
    const normalized = (text) => text?.toString().toLowerCase() || '';
    const filteredForExport = allUploads.filter(u =>
      (!statusFilter || u.status === statusFilter) &&
      (!search ||
        normalized(u.patientId).includes(normalized(search)) ||
        normalized(JSON.stringify(u.newFileName)).includes(normalized(search))
      )
    );
    
    const rows = filteredForExport.map(u => ({
      ID: u.id,
      OriginalFiles: Array.isArray(u.originalFileNames)
        ? u.originalFileNames.join(', ')
        : u.originalFileNames,
      NewFiles: Array.isArray(u.newFileName)
        ? u.newFileName.join(', ')
        : u.newFileName,
      Patient: u.patientId,
      Analysis: u.analysisCode,
      Sample: u.sampleCode,
      Project: u.projectId,
      Visit: u.visitNumber,
      Status: u.status,
      UploadTime: u.upload_time,
      Initiated: u.initiated_time,
      Completed: u.completed_time,
      FileSize: JSON.stringify(u.fileSize),
      Details: u.details,
      ResultCode: u.resultCode
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Uploads');
    XLSX.writeFile(wb, 'upload_log.xlsx');
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 300);
  };

  const modalContent = (
    <div className={`UHM-modal-overlay${isClosing ? ' closing' : ''}`} onClick={handleClose}>
      <div className={`UHM-modal ${isClosing ? 'closing' : ''}`} onClick={e => e.stopPropagation()}>
        <div className="UHM-header">
          <div className="UHM-header-title">
            <h2>Upload History</h2>
            {loading && <span className="UHM-loading">Loading...</span>}
            {error && <span className="UHM-error">Error: {error}</span>}
          <div className="UHM-controls">
          <div className="UHM-search-box">
            <FontAwesomeIcon icon={faSearch} className="UHM-search-icon" />
            <input
              type="text"
              placeholder="Search uploads..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <select
            className="UHM-status-filter"
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
          >
            <option value="">All statuses</option>
            <option value="Downloading">Downloading</option>
            <option value="Queued">Queued</option>
            <option value="Processing">Processing</option>
            <option value="Complete">Complete</option>
            <option value="DownloadFailed">Download Failed</option>
            <option value="Failed">Failed</option>
          </select>
        </div>
          </div>
     
          <div className="UHM-header-buttons">
            <button className="UHM-export-button" onClick={exportToExcel} disabled={loading}>
              <FontAwesomeIcon icon={faFileExport} />
            </button>
            <button className="UHM-refresh-button" onClick={fetchUploads} disabled={loading}>
              <FontAwesomeIcon icon={faSync} />
            </button>
            <button className="UHM-close-button" onClick={handleClose}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        </div>
      
        <div className="UTM-tabs">
          <button
            className={`UTM-tab ${activeTab === 'table' ? 'UTM-tab-active' : ''}`}
            onClick={() => setActiveTab('table')}
          >
            Table
          </button>

        </div>

        {activeTab === 'table' ? (
        <div className="UHM-table-container" ref={tableContainerRef}>
          <table className="UHM-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Status</th>

                <th>Original Files</th>
                <th>File Sizes</th>

                <th>New Files</th>
                <th>Patient</th>
                <th>Analysis</th>
                <th>Sample</th>
                <th>Project</th>
                <th>Visit</th>
                <th>Upload Time</th>
                <th>Pipelines Initiated</th>
                <th>Pipelines Completed</th>
                <th>Result</th>
              </tr>
            </thead>
            <tbody>
              {displayedUploads.map((upload, index) => (
                <tr key={`${upload.id}-${index}`}>
                  <td>{upload.id}</td>
                  <td>
                    <span
                      className="UHM-status-pill"
                      style={{
                        backgroundColor: `${statusColors[upload.status]}20`,
                        color: statusColors[upload.status] || '#333'
                      }}
                    >
                      {upload.status}
                    </span>
                  </td>
                  <td>
                    {Array.isArray(upload.originalFileNames) ? (
                      <ul className="UHM-file-list">
                        {upload.originalFileNames.map((f, i) => (
                          <li key={i}>{f}</li>
                        ))}
                      </ul>
                    ) : (
                      upload.originalFileNames
                    )}
                  </td>
                  <td style={{ display: 'flex', flexDirection: 'column' }}>
  {Array.isArray(upload.fileSize)
    ? upload.fileSize.map((fs, i) => (
        <span key={i}>
          {typeof fs === 'object' ? `${fs.gb} GB` : fs}
        </span>
      ))
    : <span>{upload.fileSize}</span>}
</td>

                  <td>
                    {Array.isArray(upload.newFileName) ? (
                      <ul className="UHM-file-list">
                        {upload.newFileName.map((f, i) => (
                          <li key={i}>{f}</li>
                        ))}
                      </ul>
                    ) : (
                      upload.newFileName
                    )}
                  </td>
                  <td>{upload.patientId}</td>
                  <td>{upload.analysisCode}</td>
                  <td>{upload.sampleCode}</td>
                  
                  <td>{upload.projectId}</td>
                  <td>{upload.visitNumber}</td>
           
                  <td>{new Date(upload.upload_time).toLocaleString()}</td>
                  <td>{upload.initiated_time && new Date(upload.initiated_time).toLocaleString()}</td>
                  <td>{upload.completed_time && new Date(upload.completed_time).toLocaleString()}</td>

                  <td>{upload.resultCode}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {loading && page === 0 && <div className="UHM-loading-more">Loading uploads...</div>}
          {loading && page > 0 && <div className="UHM-loading-more">Loading more data...</div>}
          {!hasMore && displayedUploads.length > 0 && <div className="UHM-end-message">End of results</div>}
          {!loading && displayedUploads.length === 0 && allUploads.length > 0 && (search.trim() !== '' || statusFilter !== '') &&
            <div className="UHM-no-results">No uploads match your search</div>
          }
        </div>
        ) : (
          <UploadDashboard uploads={allUploads} />
        )}
      </div>
    </div>
  );
  
  // Use createPortal to render modal directly to document body
  return createPortal(modalContent, document.body);
}

export default UploadTableModal;
